import { useState, useRef, useEffect, useCallback, type ComponentProps } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Bot,
  Send,
  Plus,
  MessageSquare,
  Trash2,
  Copy,
  Check,
  Square,
  ChevronLeft,
  Sparkles,
  PanelLeftClose,
  PanelLeft,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
} from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/hooks/useSession';
import { mastraClient } from '@/lib/mastra-client';
import { useVoice } from '../hooks/useVoice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

type AgentStreamMessages = Parameters<ReturnType<typeof mastraClient.getAgent>['stream']>[0];
type AgentStreamMessage = Extract<AgentStreamMessages, readonly unknown[]>[number];

interface Thread {
  id: string;
  title: string;
  agentId: string;
  messages: ChatMessage[];
  createdAt: number;
}

interface MastraAgent {
  id: string;
  name: string;
  description: string;
  instructions: string;
  tools: Record<string, { id: string; description: string }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function formatName(id: string) {
  return id
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[-_ ]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getThreadsKey(agentId: string) {
  return `atek-agent-threads-${agentId}`;
}

function loadThreads(agentId: string): Thread[] {
  try {
    const raw = localStorage.getItem(getThreadsKey(agentId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveThreads(agentId: string, threads: Thread[]) {
  localStorage.setItem(getThreadsKey(agentId), JSON.stringify(threads));
}

function toAgentStreamMessage(message: ChatMessage): AgentStreamMessage {
  if (message.role === 'user') {
    return { role: 'user', content: message.content };
  }

  return { role: 'assistant', content: message.content };
}

function groupThreadsByDate(threads: Thread[]): { label: string; threads: Thread[] }[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86_400_000;
  const week = today - 7 * 86_400_000;

  const groups: Record<string, Thread[]> = {
    Today: [],
    Yesterday: [],
    'Previous 7 days': [],
    Older: [],
  };

  for (const t of threads) {
    if (t.createdAt >= today) groups.Today.push(t);
    else if (t.createdAt >= yesterday) groups.Yesterday.push(t);
    else if (t.createdAt >= week) groups['Previous 7 days'].push(t);
    else groups.Older.push(t);
  }

  return Object.entries(groups)
    .filter(([, list]) => list.length > 0)
    .map(([label, list]) => ({ label, threads: list }));
}

const AGENT_SUGGESTIONS: Record<string, string[]> = {
  bookkeeper: [
    'Show outstanding invoices over $1,000',
    'Run a P&L for last quarter',
    'What is our current AR aging?',
    'Find recent payments from a customer',
  ],
  'general-assistant': [
    'Help me draft a professional email',
    'Summarize a technical concept',
    'Brainstorm ideas for a project',
    'Analyze pros and cons of an approach',
  ],
  'ticket-solver': [
    'Sensor not reporting data since yesterday',
    'Alert not triggering for high temperature',
    'User cannot log into the platform',
    'Report generation is failing',
  ],
};

const DEFAULT_SUGGESTIONS = [
  'Tell me about yourself',
  'What can you help me with?',
  'How does this work?',
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={copy}
      className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
      title="Copy"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function CodeBlock({ children, className }: ComponentProps<'code'> & { className?: string }) {
  const match = /language-(\w+)/.exec(className || '');
  const lang = match?.[1];
  const code = String(children).replace(/\n$/, '');

  if (!className) {
    return (
      <code className="bg-gray-800 text-amber-300 px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    );
  }

  return (
    <div className="relative group/code my-3">
      <div className="flex items-center justify-between bg-gray-800 rounded-t-md px-3 py-1.5">
        <span className="text-xs text-gray-400 font-mono">{lang || 'code'}</span>
        <CopyButton text={code} />
      </div>
      <pre className="bg-gray-900 rounded-b-md p-3 overflow-x-auto">
        <code className="text-amber-300 text-xs font-mono">{code}</code>
      </pre>
    </div>
  );
}

const markdownComponents = {
  code: CodeBlock,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AgentChatPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const resourceId = user?.email ?? 'anonymous';

  const { data: agentData } = useQuery({
    queryKey: ['mastra-agent', agentId],
    queryFn: async (): Promise<MastraAgent> => {
      const res = await mastraClient.getAgent(agentId!).details();
      return res as unknown as MastraAgent;
    },
    enabled: !!agentId,
  });

  const [threads, setThreads] = useState<Thread[]>(() => loadThreads(agentId ?? ''));
  const [activeThreadId, setActiveThreadId] = useState<string | null>(
    () => threads[0]?.id ?? null,
  );
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const ttsKey = `atek-agent-tts-${agentId ?? ''}`;
  const [autoSpeak, setAutoSpeak] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(ttsKey) === 'true';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(ttsKey, String(autoSpeak));
  }, [ttsKey, autoSpeak]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

  const voice = useVoice({
    onTranscript: (text) => setInput((prev) => (prev ? `${prev} ${text}` : text)),
  });
  const lastSpokenRef = useRef<string>('');

  const activeThread = threads.find((t) => t.id === activeThreadId) ?? null;
  const messages = activeThread?.messages ?? [];
  const agentName = agentData?.name || formatName(agentId ?? '');
  const suggestions = AGENT_SUGGESTIONS[agentId ?? ''] ?? DEFAULT_SUGGESTIONS;

  // Persist threads
  useEffect(() => {
    if (agentId) saveThreads(agentId, threads);
  }, [threads, agentId]);

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-speak finalized assistant messages when TTS toggle is on. Each
  // assistant message is identified by its content; once the stream lands and
  // the content is final, speak it once. Skip if user toggles off mid-stream.
  useEffect(() => {
    if (!autoSpeak || isStreaming) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || !last.content) return;
    if (lastSpokenRef.current === last.content) return;
    lastSpokenRef.current = last.content;
    voice.speak(last.content);
  }, [autoSpeak, isStreaming, messages, voice]);

  // Auto-resize textarea
  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  // Focus textarea on thread change
  useEffect(() => {
    textareaRef.current?.focus();
  }, [activeThreadId]);

  // ---- Thread management ----

  const createThread = () => {
    const thread: Thread = {
      id: generateId(),
      title: 'New conversation',
      agentId: agentId ?? '',
      messages: [],
      createdAt: Date.now(),
    };
    setThreads((prev) => [thread, ...prev]);
    setActiveThreadId(thread.id);
  };

  const deleteThread = (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setThreads((prev) => prev.filter((t) => t.id !== threadId));
    if (activeThreadId === threadId) {
      setActiveThreadId(threads.find((t) => t.id !== threadId)?.id ?? null);
    }
  };

  const updateThreadMessages = (threadId: string, updater: (msgs: ChatMessage[]) => ChatMessage[]) => {
    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId ? { ...t, messages: updater(t.messages) } : t,
      ),
    );
  };

  const updateThreadTitle = (threadId: string, title: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === threadId ? { ...t, title } : t)),
    );
  };

  // ---- AI title generation ----

  const autoGenerateTitle = async (threadId: string, userMessage: string, assistantMessage: string) => {
    try {
      const data = await mastraClient.getAgent('general-assistant').generate(
        [
          {
            role: 'user',
            content: `Generate a short 4-6 word title for this conversation. Reply with ONLY the title, no punctuation, no quotes.\n\nUser: ${userMessage}\nAssistant: ${assistantMessage.slice(0, 200)}`,
          },
        ],
        {
          memory: { thread: `title-gen-${threadId}`, resource: resourceId },
        } as unknown as Parameters<ReturnType<typeof mastraClient.getAgent>['generate']>[1],
      );
      const d = data as { text?: string; content?: string };
      const title = (d?.text || d?.content || '').trim().replace(/^["']|["']$/g, '');
      if (title) updateThreadTitle(threadId, title);
    } catch {
      // silently fail
    }
  };

  // ---- Stop streaming ----

  const stopStreaming = () => {
    readerRef.current?.cancel().catch(() => { /* swallow */ });
  };

  // ---- Send message ----

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isStreaming) return;

    let threadId = activeThreadId;
    if (!threadId) {
      const thread: Thread = {
        id: generateId(),
        title: text.length > 40 ? text.slice(0, 40) + '...' : text,
        agentId: agentId ?? '',
        messages: [],
        createdAt: Date.now(),
      };
      setThreads((prev) => [thread, ...prev]);
      setActiveThreadId(thread.id);
      threadId = thread.id;
    }

    const userMessage: ChatMessage = { role: 'user', content: text };
    const currentThread = threads.find((t) => t.id === threadId);
    const currentMessages = currentThread?.messages ?? [];
    const allMessages = [...currentMessages, userMessage];
    const isFirstExchange = currentMessages.length === 0;

    if (isFirstExchange) {
      updateThreadTitle(threadId, text.length > 40 ? text.slice(0, 40) + '...' : text);
    }

    updateThreadMessages(threadId, () => allMessages);
    setInput('');
    setIsStreaming(true);

    updateThreadMessages(threadId, (msgs) => [...msgs, { role: 'assistant', content: '' }]);

    let assistantResponse = '';

    try {
      const res = await mastraClient.getAgent(agentId!).stream(
        allMessages.map(toAgentStreamMessage),
        {
          memory: { thread: threadId, resource: resourceId },
        } as unknown as Parameters<ReturnType<typeof mastraClient.getAgent>['stream']>[1],
      );

      if (!res.ok) {
        const errorText = await res.text().catch(() => res.statusText);
        throw new Error(errorText || `Agent error: ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');
      readerRef.current = reader;

      const decoder = new TextDecoder();
      let buffer = '';
      const tid = threadId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          try {
            const event = JSON.parse(raw);
            if (event.type === 'text-delta' && event.payload?.text) {
              assistantResponse += event.payload.text;
              updateThreadMessages(tid, (msgs) => {
                const updated = [...msgs];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + event.payload.text,
                  };
                }
                return updated;
              });
            }
          } catch {
            // skip
          }
        }
      }

      if (isFirstExchange && assistantResponse) {
        autoGenerateTitle(threadId, text, assistantResponse);
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // Stopped by user — keep partial response
      } else {
        const tid = threadId;
        updateThreadMessages(tid, (msgs) => {
          const updated = [...msgs];
          const last = updated[updated.length - 1];
          if (last?.role === 'assistant' && !last.content) {
            updated[updated.length - 1] = {
              ...last,
              content: `Error: ${err instanceof Error ? err.message : 'Failed to get response'}`,
            };
          }
          return updated;
        });
      }
    } finally {
      readerRef.current = null;
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ---- Render ----

  const threadGroups = groupThreadsByDate(threads);

  // Break out of .admin-content padding and fill the full available area
  return (
    <div className="flex -m-5 overflow-hidden" style={{ height: 'calc(100% + 40px)' }}>
      {/* ── Sidebar ── */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } transition-all duration-200 border-r border-gray-200 bg-gray-50 flex flex-col shrink-0 overflow-hidden`}
      >
        <div className="p-3 border-b border-gray-200 flex items-center justify-between min-w-[256px]">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 justify-start gap-2 text-sm h-8 bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={createThread}
          >
            <Plus className="h-3.5 w-3.5" />
            New chat
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 ml-1 shrink-0 text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(false)}
          >
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto py-2 min-w-[256px]">
          {threads.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-8 px-4">
              No conversations yet.
              <br />
              Start typing below to begin.
            </div>
          )}
          {threadGroups.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="px-3 py-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {group.label}
              </div>
              {group.threads.map((thread) => (
                <div
                  key={thread.id}
                  onClick={() => setActiveThreadId(thread.id)}
                  className={`group flex items-center gap-2 mx-2 px-2.5 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                    activeThreadId === thread.id
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'
                  }`}
                >
                  <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-40" />
                  <span className="truncate flex-1">{thread.title}</span>
                  <button
                    onClick={(e) => deleteThread(thread.id, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="p-2 border-t border-gray-200 min-w-[256px]">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-xs text-gray-500 hover:text-gray-700"
            onClick={() => navigate('/ai/agents')}
          >
            <ChevronLeft className="h-3 w-3" />
            All agents
          </Button>
        </div>
      </div>

      {/* ── Chat area ── */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden bg-white">
        {/* Header */}
        <div className="shrink-0 h-12 border-b border-gray-200 flex items-center gap-2 px-4 bg-white">
          {!sidebarOpen && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-blue-100 flex items-center justify-center">
              <Bot className="h-3.5 w-3.5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-900">{agentName}</span>
            {agentData?.description && (
              <span className="text-xs text-gray-400 hidden sm:inline">
                — {agentData.description}
              </span>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="max-w-3xl mx-auto px-4 py-6">
            {messages.length === 0 ? (
              /* ── Empty state ── */
              <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center">
                    <Sparkles className="h-7 w-7 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{agentName}</h2>
                    {agentData?.description && (
                      <p className="text-sm text-gray-500 mt-1 max-w-md">
                        {agentData.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                  {suggestions.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      className="text-left text-sm px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-gray-600 hover:text-gray-900 shadow-sm"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* ── Message list ── */
              <div className="space-y-6">
                {messages.map((msg, i) => (
                  <div key={i} className="group/msg">
                    {msg.role === 'user' ? (
                      /* ── User message ── */
                      <div className="flex justify-end">
                        <div className="max-w-[80%] bg-blue-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap shadow-sm">
                          {msg.content}
                        </div>
                      </div>
                    ) : (
                      /* ── Assistant message ── */
                      <div>
                        {msg.content ? (
                          <div className="prose prose-sm max-w-none text-gray-800
                            [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
                            [&_p]:my-2 [&_p]:leading-relaxed
                            [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5
                            [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-gray-900 [&_h1]:mt-4 [&_h1]:mb-2
                            [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-3 [&_h2]:mb-1.5
                            [&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-gray-900 [&_h3]:mt-3 [&_h3]:mb-1
                            [&_a]:text-blue-600 [&_a]:no-underline [&_a:hover]:underline
                            [&_strong]:text-gray-900 [&_strong]:font-semibold
                            [&_table]:text-xs [&_table]:my-3 [&_table]:border [&_table]:border-gray-200 [&_table]:rounded
                            [&_th]:px-3 [&_th]:py-1.5 [&_th]:bg-gray-50 [&_th]:border-b [&_th]:border-gray-200 [&_th]:text-left [&_th]:font-medium [&_th]:text-gray-700
                            [&_td]:px-3 [&_td]:py-1.5 [&_td]:border-b [&_td]:border-gray-100
                            [&_blockquote]:border-l-2 [&_blockquote]:border-blue-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-500
                            [&_hr]:border-gray-200 [&_hr]:my-4
                            text-sm"
                          >
                            <Markdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {msg.content}
                            </Markdown>
                          </div>
                        ) : isStreaming && i === messages.length - 1 ? (
                          <div className="flex items-center gap-2 py-2">
                            <div className="flex gap-1">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:0ms]" />
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:150ms]" />
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:300ms]" />
                            </div>
                            <span className="text-xs text-gray-400">Thinking...</span>
                          </div>
                        ) : null}

                        {/* Message actions */}
                        {msg.content && !(isStreaming && i === messages.length - 1) && (
                          <div className="flex items-center gap-1 mt-1 opacity-0 group-hover/msg:opacity-100 transition-opacity">
                            <CopyButton text={msg.content} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* ── Input area ── */}
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
          <div className="max-w-3xl mx-auto">
            <div className="relative flex items-end gap-2 bg-gray-50 border border-gray-300 rounded-2xl px-4 py-2.5 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all shadow-sm">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Message ${agentName}...`}
                className="flex-1 bg-transparent border-none outline-none resize-none text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 min-h-[24px] max-h-[200px] py-0.5"
                rows={1}
                disabled={isStreaming}
              />
              {voice.ttsSupported && (
                <Button
                  onClick={() => {
                    if (voice.speaking) voice.cancelSpeak();
                    setAutoSpeak((v) => !v);
                  }}
                  variant="ghost"
                  size="icon"
                  className={`shrink-0 h-8 w-8 rounded-lg ${autoSpeak ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-500 hover:bg-gray-100'}`}
                  title={autoSpeak ? 'Auto-speak replies: on (click to mute)' : 'Auto-speak replies: off (click to enable)'}
                >
                  {autoSpeak ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                </Button>
              )}
              {voice.sttSupported && (
                <Button
                  onClick={() => (voice.listening ? voice.stopListening() : voice.startListening())}
                  variant="ghost"
                  size="icon"
                  className={`shrink-0 h-8 w-8 rounded-lg ${voice.listening ? 'text-red-500 hover:bg-red-50 animate-pulse' : 'text-gray-500 hover:bg-gray-100'}`}
                  title={voice.listening ? 'Stop listening' : 'Voice input (speak to dictate)'}
                  disabled={isStreaming}
                >
                  {voice.listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                </Button>
              )}
              {isStreaming ? (
                <Button
                  onClick={stopStreaming}
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-500"
                  title="Stop generating"
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                </Button>
              ) : (
                <Button
                  onClick={() => sendMessage()}
                  disabled={!input.trim()}
                  size="icon"
                  className="shrink-0 h-8 w-8 rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-30 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <p className="text-[11px] text-gray-400 text-center mt-2">
              Enter to send · Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
